/*─────────────────────────────────────────────────────────────
  utils/timezone.ts      – compatible with date-fns-tz v1.x
─────────────────────────────────────────────────────────────*/
import {
  fromZonedTime,   //  ← v1 name for zonedTimeToUtc
  toZonedTime,     //  ← v1 name for utcToZonedTime
  format as formatTz,
} from 'date-fns-tz';
import { parse, isValid } from 'date-fns';

/** Try 24-hour first, then 12-hour AM/PM. */
function parseLocalDateTime(dateISO: string, timeStr: string): Date {
  // 1) yyyy-MM-dd HH:mm    (24-hour)
  let d = parse(`${dateISO} ${timeStr}`, 'yyyy-MM-dd HH:mm', new Date());
  if (isValid(d)) return d;

  // 2) yyyy-MM-dd h:mm a   (12-hour)
  d = parse(`${dateISO} ${timeStr}`, 'yyyy-MM-dd h:mm a', new Date());
  return d;                // may be Invalid Date – caller must check
}

/**
 * Convert local clock time → UTC Date.
 * @param localDate yyyy-MM-dd
 * @param localTime "HH:mm" **or** "h:mm a"
 * @param timezone  IANA zone, e.g. "America/New_York"
 */
export const convertToUTC = (
  localDate: string,
  localTime: string,
  timezone: string
): Date => {
  const local = parseLocalDateTime(localDate, localTime);
  if (!isValid(local)) return new Date(NaN);
  return fromZonedTime(local, timezone);   // ← same as zonedTimeToUtc
};

/**
 * Convert UTC → local date & time strings in the given TZ.
 */
export const convertFromUTC = (utcDate: Date, timezone: string) => {
  const zoned = toZonedTime(utcDate, timezone); // ← same as utcToZonedTime
  return {
    localDate: formatTz(zoned, 'yyyy-MM-dd', { timeZone: timezone }),
    localTime: formatTz(zoned, 'h:mm a',     { timeZone: timezone }),
  };
};

/** Browser best-guess for the user’s IANA time-zone. */
export const getUserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;
